import styled from 'styled-components';
import { AiFillInfoCircle } from 'react-icons/ai';
import CloseButton from 'react-bootstrap/CloseButton';
import { useEffect, useCallback } from 'react';

import { useProject } from '@contexts/Project.context';

// Constants
const DEFAULT_AUTO_CLOSE_DURATION = 5000; // 5 seconds
const DIALOG_TOP_OFFSET = 69; // px - offset from navbar
const DIALOG_Z_INDEX = 50;
const HEADER_MAX_HEIGHT = 80; // px
const HEADER_LINE_CLAMP = 2;

const DialogueBox = styled.div`
	position: fixed;
	top: ${DIALOG_TOP_OFFSET}px;
	right: 8px;
	width: ${({ $fullwidth }) => ($fullwidth ? 'calc(100% - 16px)' : '20%')};
	min-width: ${({ $fullwidth }) => ($fullwidth ? 'auto' : '280px')};
	max-height: 50vh;
	background-color: ${props => `rgba(${props.theme.colors.backgroundRgb}, 1)`};
	color: ${props => props.theme.colors.textColor};
	z-index: ${DIALOG_Z_INDEX};
	display: flex;
	flex-direction: column;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	border-radius: 8px;
	overflow: hidden;

	& h2 {
		text-transform: capitalize;
		font-weight: 600;
		display: flex;
		align-items: flex-start;
		gap: 10px;
		margin: 0;
		padding: 20px 20px 10px 20px;
		flex-shrink: 0;
		font-size: 1.25rem;
		line-height: 1.4;
		word-break: break-word;
		max-height: ${HEADER_MAX_HEIGHT}px;
		overflow: hidden;

		& svg {
			flex-shrink: 0;
			font-size: 1.5rem;
			margin-top: 3px;
		}

		& span {
			display: -webkit-box;
			-webkit-line-clamp: ${HEADER_LINE_CLAMP};
			-webkit-box-orient: vertical;
			overflow: hidden;
			text-overflow: ellipsis;
		}
	}

	& p {
		margin-bottom: 0;
	}
`;

const DialogueHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 12px;
	padding: 20px 20px 10px 20px;
	flex-shrink: 0;

	& h2 {
		padding: 0;
		flex: 1;
		min-width: 0;
	}

	& button {
		flex-shrink: 0;
		margin-top: 2px;
	}
`;

const DialogueContent = styled.div`
	padding: 0 20px 20px 20px;
	overflow-y: auto;
	flex: 1;
`;

/**
 * Dialogue Component
 *
 * Displays a dialog box with configurable positioning, auto-close, and accessibility features.
 *
 * @typedef {Object} DialogueConfig
 * @property {boolean} [dismissible] - Whether the dialog can be closed by user
 * @property {string} header - Dialog header text
 * @property {React.ReactNode} body - Dialog content
 * @property {boolean} [fullWidth] - Use full width mode (default: compact 20% width)
 * @property {Function} [onDismiss] - Callback when dialog is dismissed
 * @property {boolean} [autoClose] - Enable auto-close functionality
 * @property {number} [autoCloseDuration=5000] - Auto-close duration in milliseconds
 *
 * @example
 * // Basic usage
 * dispatch({
 *   type: 'setDialogue',
 *   payload: {
 *     header: 'Success',
 *     body: 'Operation completed',
 *     dismissible: true
 *   }
 * });
 *
 * @example
 * // With auto-close
 * dispatch({
 *   type: 'setDialogue',
 *   payload: {
 *     header: 'Info',
 *     body: 'Quick message',
 *     autoClose: true,
 *     autoCloseDuration: 3000
 *   }
 * });
 */
const Dialogue = () => {
	const { dialogue, dispatch } = useProject();

	const {
		dismissible,
		header,
		body,
		fullWidth,
		onDismiss,
		autoClose,
		autoCloseDuration = DEFAULT_AUTO_CLOSE_DURATION,
	} = dialogue || {};

	const handleClose = useCallback(() => {
		dispatch({ type: 'setDialogue', payload: null });
		onDismiss && onDismiss();
	}, [dispatch, onDismiss]);

	// Auto-close functionality
	useEffect(() => {
		if (dialogue && autoClose) {
			// Track mount status to prevent state updates after unmount
			// Prevents React warning if component unmounts before timer fires
			let isMounted = true;

			const timer = setTimeout(() => {
				// Only close if component is still mounted
				if (isMounted) handleClose();
			}, autoCloseDuration);

			return () => {
				isMounted = false; // Mark as unmounted
				clearTimeout(timer);
			};
		}
	}, [dialogue, autoClose, autoCloseDuration, handleClose]);

	return dialogue ? (
		<DialogueBox
			$fullwidth={fullWidth}
			role="dialog"
			aria-modal="true"
			aria-labelledby="dialogue-header"
			aria-describedby="dialogue-content">
			{dismissible ? (
				<DialogueHeader>
					<h2 id="dialogue-header">
						<AiFillInfoCircle />{' '}
						{/* title attribute shows full text on hover when truncated */}
						<span title={header}>{header}</span>
					</h2>
					<CloseButton
						label="Close"
						variant="white"
						onClick={handleClose}
						aria-label="Close dialog"
					/>
				</DialogueHeader>
			) : (
				<h2 id="dialogue-header">
					<AiFillInfoCircle />{' '}
					{/* title attribute shows full text on hover when truncated */}
					<span title={header}>{header}</span>
				</h2>
			)}

			<DialogueContent id="dialogue-content">{body}</DialogueContent>
		</DialogueBox>
	) : null;
};

export default Dialogue;
